/**
 * Shared design-pack primitives — extracted from DesignPackTab.js as
 * part of the v260.76 refactor. No behaviour change.
 *
 * Exports: fmtDate, fmtDateTime, SectionCard, KV, STATUS_META, CATEGORY_META.
 */
import React, { useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  ChevronDown, ChevronRight,
  CircleSlash, Play, Pause, Check,
  GitBranch, ListChecks, Sparkles, Plug, ShieldCheck,
} from 'lucide-react';

// Status + category visual metadata shared by ChecklistSection and any
// downstream UI that needs to render a deliverable badge/icon pair.
const STATUS_META = {
  not_started: { label: 'Not started', tone: 'bg-slate-100 text-slate-600 border-slate-200', icon: CircleSlash },
  in_progress: { label: 'In progress', tone: 'bg-sky-100 text-sky-700 border-sky-200', icon: Play },
  blocked:     { label: 'Blocked',     tone: 'bg-rose-100 text-rose-700 border-rose-200', icon: Pause },
  complete:    { label: 'Complete',    tone: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Check },
};

const CATEGORY_META = {
  Component: { tone: 'bg-indigo-50 text-indigo-700', icon: GitBranch },
  Deliverable: { tone: 'bg-violet-50 text-violet-700', icon: ListChecks },
  'Custom Dev': { tone: 'bg-amber-50 text-amber-700', icon: Sparkles },
  Integration: { tone: 'bg-teal-50 text-teal-700', icon: Plug },
  'Sign-off': { tone: 'bg-emerald-50 text-emerald-700', icon: ShieldCheck },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

// ─── Section components ───────────────────────────────────────────────

function SectionCard({ id, icon: Icon, title, count, children, defaultOpen = true, titleExtra = null, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card data-testid={testid || `design-pack-section-${id}`}>
      <CardHeader className="pb-3">
        <div className="w-full flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="flex-1 flex items-center justify-between text-left min-w-0"
            data-testid={`design-pack-section-toggle-${id}`}
          >
            <CardTitle className="text-base flex items-center gap-2">
              {Icon && <Icon className="w-4 h-4 text-[#4ABFCF]" />}
              {title}
              {count !== undefined && count !== null && (
                <Badge variant="secondary" className="text-[10px]">{count}</Badge>
              )}
            </CardTitle>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            {titleExtra}
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="text-slate-400 hover:text-slate-600"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </CardHeader>
      {open && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
}

function KV({ label, value, testid }) {
  return (
    <div className="flex flex-col gap-0.5" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
      <span className="text-sm text-[#0F2040]">{value || '—'}</span>
    </div>
  );
}

export { fmtDate, fmtDateTime, SectionCard, KV, STATUS_META, CATEGORY_META };
